import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';

const Comunicados = () => {
  const [comunicados, setComunicados] = useState([]);
  const [comunicadoSelecionado, setComunicadoSelecionado] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarComunicados();
  }, []);
  
  const carregarComunicados = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('announcements')
        .select('*, admins(name)')
        .eq('active', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Transformar os dados para o formato esperado pelo componente
      const comunicadosFormatados = data.map(item => ({
        id: item.id,
        titulo: item.title,
        conteudo: item.content,
        tipo: item.type || 'geral',
        prioridade: item.priority || 'baixa',
        data: item.created_at,
        autor: item.admins?.name || 'Administrador'
      }));
      
      setComunicados(comunicadosFormatados);
    } catch (error) {
      console.error('Erro ao carregar comunicados:', error);
      toast.error('Erro ao carregar os comunicados. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  const tiposComunicado = [
    { value: 'todos', label: 'Todos', icon: '📢' },
    { value: 'geral', label: 'Geral', icon: '📋' },
    { value: 'inscricoes', label: 'Inscrições', icon: '✍️' },
    { value: 'quiz', label: 'Quiz', icon: '🏆' },
    { value: 'alteracao', label: 'Alterações', icon: '⚠️' },
    { value: 'material', label: 'Material', icon: '📚' },
    { value: 'certificados', label: 'Certificados', icon: '🎓' },
    { value: 'transmissao', label: 'Transmissão', icon: '📺' },
    { value: 'avaliacao', label: 'Avaliação', icon: '📝' },
    { value: 'vagas', label: 'Vagas', icon: '🎯' },
    { value: 'encerramento', label: 'Encerramento', icon: '🏁' }
  ];

  const comunicadosFiltrados = filtroTipo === 'todos' 
    ? comunicados 
    : comunicados.filter(comunicado => comunicado.tipo === filtroTipo);

  const formatarData = (data) => {
    try {
      if (!data) return 'Data não disponível';
      
      // Criar objeto de data a partir do timestamp do Supabase
      const dataObj = new Date(data);
      
      if (isNaN(dataObj.getTime())) return 'Data inválida';
      
      return dataObj.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return 'Erro na data';
    }
  };

  const getIconeTipo = (tipo) => {
    const icones = {
      'geral': '📋',
      'inscricoes': '✍️',
      'quiz': '🏆',
      'alteracao': '⚠️',
      'material': '📚',
      'certificados': '🎓',
      'transmissao': '📺',
      'avaliacao': '📝',
      'vagas': '🎯',
      'encerramento': '🏁'
    };
    return icones[tipo] || '📢';
  };

  const getPrioridadeCor = (prioridade) => {
    const cores = {
      'urgent': 'bg-red-50 border-red-200',
      'high': 'bg-orange-50 border-orange-200',
      'normal': 'bg-yellow-50 border-yellow-200',
      'low': 'bg-green-50 border-green-200'
    };
    return cores[prioridade] || 'bg-gray-50 border-gray-200';
  };

  const getPrioridadeBorda = (prioridade) => {
    const bordas = {
      'urgent': 'border-red-300',
      'high': 'border-orange-300',
      'normal': 'border-yellow-300',
      'low': 'border-green-300'
    };
    return bordas[prioridade] || 'border-gray-200';
  };

  const getPrioridadeTexto = (prioridade) => {
    const textos = {
      'urgent': 'Urgente',
      'high': 'Alta Prioridade',
      'normal': 'Média Prioridade',
      'low': 'Baixa Prioridade'
    };
    return textos[prioridade] || 'Normal';
  };

  return (
    <div className="min-h-screen py-8 w-full">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-amazonia-dark-800 mb-4">
            Comunicados e Avisos
          </h1>
        </motion.div>

        {/* Filtros */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
        </motion.div>

        {/* Lista de Comunicados */}
        <div className="space-y-6">
          {comunicadosFiltrados.map((comunicado, index) => (
            <motion.div
              key={comunicado.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`bg-white rounded-xl shadow-lg border-l-4 ${getPrioridadeBorda(comunicado.prioridade)} hover:shadow-xl transition-all duration-300 cursor-pointer`}
              onClick={() => setComunicadoSelecionado(comunicado)}
            >
              <div className="p-6">
                <div className="flex flex-col sm:flex-row items-start justify-between mb-4 gap-4">
                  <div className="flex-grow">
                    <h3 className="text-xl font-bold text-amazonia-dark-800 mb-1">
                      {comunicado.titulo}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                      <span className="flex items-center space-x-1">
                        <span>{formatarData(comunicado.data)}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <span>Enviado por: {comunicado.autor}</span>
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        comunicado.prioridade === 'alta' ? 'bg-red-100 text-red-800' :
                        comunicado.prioridade === 'media' ? 'bg-golden-100 text-golden-800' :
                        'bg-amazonia-green-100 text-amazonia-green-800'
                      }`}>
                        {getPrioridadeTexto(comunicado.prioridade)}
                      </span>
                    </div>
                  </div>
                  
                  <button className="bg-amazonia-green-600 hover:bg-amazonia-green-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium w-full sm:w-auto flex-shrink-0">
                    Ler Mais
                  </button>
                </div>
                
                <p className="text-gray-700 leading-relaxed line-clamp-3">
                  {comunicado.conteudo}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Modal de Detalhes */}
        {comunicadoSelecionado && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setComunicadoSelecionado(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`${getPrioridadeCor(comunicadoSelecionado.prioridade)} text-gray-800 p-6 rounded-t-xl border-b`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-3xl text-gray-700 border-2 border-gray-200">
                      {getIconeTipo(comunicadoSelecionado.tipo)}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold mb-2">
                        {comunicadoSelecionado.titulo}
                      </h2>
                      <div className="flex items-center space-x-4 text-white text-opacity-90">
                        <span className="flex items-center space-x-1">
                          <span>📅</span>
                          <span>{formatarData(comunicadoSelecionado.data)}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <span>👤</span>
                          <span>{comunicadoSelecionado.autor}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setComunicadoSelecionado(null)}
                    className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="mb-6">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    comunicadoSelecionado.prioridade === 'alta' ? 'bg-red-100 text-red-800' :
                    comunicadoSelecionado.prioridade === 'media' ? 'bg-golden-100 text-golden-800' :
                    'bg-amazonia-green-100 text-amazonia-green-800'
                  }`}>
                    {getPrioridadeTexto(comunicadoSelecionado.prioridade)}
                  </span>
                </div>
                
                <div className="prose max-w-none">
                  <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-line">
                    {comunicadoSelecionado.conteudo}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Estado de carregamento */}
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="flex justify-center mb-4">
              <Loader className="w-12 h-12 text-amazonia-green-600 animate-spin" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600">
              Carregando comunicados...
            </h3>
          </motion.div>
        )}
        
        {/* Mensagem quando não há comunicados */}
        {!loading && comunicadosFiltrados.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="text-6xl mb-4">📢</div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              Nenhum comunicado encontrado
            </h3>
            <p className="text-gray-500">
              Tente selecionar um filtro diferente para ver mais comunicados.
            </p>
          </motion.div>
        )}

        {/* Estatísticas */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12 bg-white rounded-xl shadow-lg p-6"
        >
          <h3 className="text-lg font-semibold text-amazonia-dark-800 mb-4">
            Resumo dos Comunicados
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-amazonia-green-600">
                {comunicados.length}
              </div>
              <div className="text-sm text-gray-600">Total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {comunicados.filter(c => c.prioridade === 'alta').length}
              </div>
              <div className="text-sm text-gray-600">Alta Prioridade</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-golden-600">
                {comunicados.filter(c => c.prioridade === 'media').length}
              </div>
              <div className="text-sm text-gray-600">Média Prioridade</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-amazonia-green-600">
                {comunicados.filter(c => c.prioridade === 'baixa').length}
              </div>
              <div className="text-sm text-gray-600">Baixa Prioridade</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Comunicados;
